import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FormPageLayout } from '@components/common';
import RefillForm from '@components/common/forms/RefillForm';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';
import api from '@services/api';

export const ResourceRefillPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const notify = useNotification();

    const [resource, setResource] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchResource = async () => {
            if (!id) return;

            try {
                setLoading(true);
                const response = await api.get(`/resources/${id}`);
                setResource(response.data);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch resource:', err);
                setError(t('resources.resourceRefill.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        fetchResource();
    }, [id, t]);

    const handleSubmit = async (formData) => {
        try {
            const response = await api.refills.create({
                resource_id: id,
                quantity_added: formData.quantity_added,
                notes: formData.notes || null
            });
            notify.success(t('refill.added_successfully'));
            navigate(`/refills/${response.data.id}`);
        } catch (err) {
            console.error('Failed to refill resource:', err);
            notify.error(t('resources.resourceRefill.refillError'));
            throw err;
        }
    };

    const handleCancel = () => {
        navigate(`/resources/${id}`);
    };

    if (loading) {
        return (
            <FormPageLayout
                title={t('resources.resourceRefill.refillResource')}
                subtitle={t('resources.resourceRefill.loading')}
                onBack={handleCancel}
                loading={true}
            />
        );
    }

    if (error || !resource) {
        return (
            <FormPageLayout
                title={t('resources.resourceRefill.refillResource')}
                subtitle={error || t('resources.resourceRefill.notFound')}
                onBack={handleCancel}
                error={true}
            />
        );
    }

    return (
        <FormPageLayout
            title={t('resources.resourceRefill.refillResource')}
            subtitle={t('resources.resourceRefill.subtitle', { resourceName: resource.name })}
            onBack={handleCancel}
        >
            <RefillForm
                resource={resource}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
            />
        </FormPageLayout>
    );
};

export default ResourceRefillPage;
